/**
 * The shared `react-select` control (`docs/select-inputs.md`, ADR-0024).
 *
 * Every select-option input in the SPA is this component, or `react-select` reached through it. It exists so the five
 * problems every call site would otherwise solve for itself are solved once:
 *
 * 1. **direction** ΓÇö Persian is right to left and the popup has to open in the reading direction. The direction is read
 *    from the interface (`useLocale`, the same source `i18n.ts` writes to the document) and handed to the library, which
 *    uses it for the chip order, the value order and the popup placement;
 * 2. **strings** ΓÇö placeholder, empty answer, loading answer and the screen-reader announcements come from the copy
 *    catalogues, never from the library's English defaults;
 * 3. **Persian search** ΓÇö the local filter folds `█î/┘è`, `┌⌐/┘â`, the Arabic-Indic digits and the diacritics before it
 *    compares, so a user types the letter their keyboard produces;
 * 4. **shape** ΓÇö the value is always the domain identifier and the label is display text, so a stored selection restores
 *    from its identifier and a locale switch re-renders the label;
 * 5. **design system** ΓÇö colours, radius and focus ring come from the tokens in `styles/theme.css`, so dark mode and the
 *    focus treatment match every other control.
 *
 * Four properties are deliberate and worth knowing before changing anything:
 *
 * - **the menu is portalled.** `react-select` renders its menu inside the control by default, which clips it under a card,
 *   a scroll container or the bottom of the viewport ΓÇö this product is a stack of cards. `menuPortalTarget` moves it to
 *   `document.body`, and the portal keeps the control's direction so the menu still lays out right to left.
 * - **the description of a locked field travels beside the input.** `react-select` 5 sets `aria-describedby` on its own
 *   search input (to the placeholder, or to its live region), so that attribute cannot be forwarded and a hint or an error
 *   is bound with `aria-errormessage` + `aria-invalid` instead. A locked field, then, cannot describe itself: its selected
 *   value is rendered as read-only text next to the control (`DisabledValue`), which a screen reader reads in the tab order
 *   of the form rather than silently dropping with the disabled input.
 * - **an unoffered selection stays visible.** `options` and `value` are matched by identifier, so the caller appends a row
 *   for a stored identifier the list no longer offers; the control renders it as the selected label rather than showing
 *   an empty field for a record that *is* set (`docs/select-inputs.md` ┬º3).
 * - **the name does not depend on the state.** A select is named by the `<label>` it is given (`labelId` ΓåÆ
 *   `aria-labelledby`) whether it is usable or locked; a locked field, whose selected value a screen reader otherwise
 *   stops reading, describes itself with that value through a visually hidden element.
 */
import { createContext, useContext, useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Select, { components } from 'react-select';
import type {
  GroupBase,
  InputProps,
  MenuProps,
  MultiValueRemoveProps,
  StylesConfig,
  Theme,
} from 'react-select';

import { disabledValueId, matchesOptionTerm } from '@/components/ui/select-options';
import type { SelectOption } from '@/components/ui/select-options';
import { useAsyncSelectOptions } from '@/components/ui/react-select-async';
import type { AsyncSelectLoader } from '@/components/ui/react-select-async';
import { useLocale } from '@/hooks/useLocale';

/** The shape every select in this application is rendered with: one level, no groups. */
type OptionType = SelectOption;
type Group = GroupBase<OptionType>;

/**
 * How tall a server-backed select's menu may be before its rows scroll (TASK-0057).
 *
 * The library gives the option list the menu's `maxHeight` and makes it scrollable only when that height is finite, so this
 * constant is what turns the paging footer's "scroll to load more" into a gesture the menu can actually perform. One page of
 * five option rows (the page size the control asks its loader for) plus the paging strip is comfortably under this, so the
 * opening page is shown whole and the list starts scrolling exactly when a further page is worth asking for.
 */
const ASYNC_SELECT_MAX_MENU_HEIGHT = 256;

/**
 * Styles of the control, rewritten against the design tokens.
 *
 * `react-select` styles itself through Emotion with its own palette, so the Tailwind theme variables are read here as
 * CSS values (`hsl(var(--token))`). `unstyled` is deliberately **not** used: it would leave every inner part to this file,
 * including the parts whose layout the library computes (the value container's padding, the indicator widths).
 */
const SELECT_STYLES: StylesConfig<OptionType, false, Group> = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base, state) => ({
    ...base,
    minHeight: '2.5rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'hsl(var(--background))',
    borderColor: state.isFocused ? 'hsl(var(--ring))' : 'hsl(var(--input))',
    boxShadow: state.isFocused ? '0 0 0 2px hsl(var(--ring) / 0.35)' : 'none',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.5 : 1,
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  }),
  valueContainer: (base) => ({ ...base, paddingInline: '0.75rem', gap: '0.25rem' }),
  singleValue: (base) => ({ ...base, color: 'hsl(var(--foreground))', margin: 0 }),
  placeholder: (base) => ({ ...base, color: 'hsl(var(--muted-foreground))', margin: 0 }),
  input: (base) => ({ ...base, color: 'hsl(var(--foreground))', margin: 0 }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'hsl(var(--border))',
    marginBlock: '0.5rem',
  }),
  dropdownIndicator: (base) => ({ ...base, color: 'hsl(var(--muted-foreground))' }),
  clearIndicator: (base) => ({ ...base, color: 'hsl(var(--muted-foreground))' }),
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
  menu: (base) => {
    // The paged menu's footer is rendered *inside* this element (`PagedMenu`, TASK-0059), so one box carries the whole panel —
    // rows, strip, background, border, radius and shadow — and nothing is left floating below it. That is why this hook keeps
    // its full surface for the paged menu too: the earlier attempts gave the strip its own background, which only moved the
    // seam. What the paged menu needs instead is `overflow: visible` on nothing and a bounded *rows* height: see `menuList`,
    // which takes the library's `maxHeight` so the strip stays pinned while the rows scroll.
    return {
      ...base,
      backgroundColor: 'hsl(var(--popover))',
      borderRadius: 'var(--radius)',
      border: '1px solid hsl(var(--border))',
      boxShadow: '0 8px 24px hsl(var(--foreground) / 0.12)',
      // `hidden` rounds the strip with the panel. The strip is a separate element from the scrolling list, so clipping here
      // cannot cut a row in half.
      overflow: 'hidden',
    };
  },
  /**
   * Styles of the menu's option list.
   *
   * The library owns the scroll container: `menuList` receives `maxHeight` (computed from the control's `maxMenuHeight`) and
   * `overflowY: auto` in its base styles, so the list scrolls as soon as the menu has a finite height. What it cannot know is
   * that a server-backed menu puts a paging strip **under** the list and makes the menu a two-part column (TASK-0057): the
   * list then has to give that strip its space. `flex: 1 1 auto` lets the list take what is left of the column's height and
   * `minHeight: 0` overrides the flex default of "never shorter than the content", which is what would otherwise make the
   * list grow past its `maxHeight` and push the footer out of the menu instead of scrolling.
   */
  menuList: (base) => ({
    ...base,
    paddingBlock: '0.25rem',
    flex: '1 1 auto',
    minHeight: 0,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    fontWeight: state.isSelected ? 500 : 400,
    color: state.isDisabled ? 'hsl(var(--muted-foreground))' : 'hsl(var(--popover-foreground))',
    backgroundColor: state.isSelected
      ? 'hsl(var(--primary-muted))'
      : state.isFocused
        ? 'hsl(var(--accent))'
        : 'transparent',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    ':active': {
      backgroundColor: state.isSelected ? 'hsl(var(--primary-muted))' : 'hsl(var(--accent))',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'hsl(var(--muted-foreground))',
    fontSize: '0.875rem',
  }),
  loadingMessage: (base) => ({
    ...base,
    color: 'hsl(var(--muted-foreground))',
    fontSize: '0.875rem',
  }),
};

/**
 * The same styles for the multi-value mode.
 *
 * `react-select` types its `styles` map against the narrowest control it supports (`isMulti: false`), so the multi-value
 * mode widens it here once instead of restyling the control a second time. Nothing is re-derived from the base styles: the
 * library's own multi-value layouts (the padding of a field holding chips, the width of the indicators) stay as the
 * library computes them, which is the rule the module note gives for `unstyled` as well.
 */
const MULTI_STYLES = SELECT_STYLES as unknown as StylesConfig<OptionType, true, Group>;

/**
 * Styles for a control that reads but does not take a choice, on top of the shared ones.
 *
 * The style map is the only place a caller reaches a part the library builds itself, and the library resolves it after its
 * own attributes, so a flag declared here is what that element finally carries.
 *
 * The **popup** needs the flag: it is the field's only listbox, it holds nothing while the menu is closed, and once it
 * opens it is the element a screen reader checks before exploring the options. It is marked here ΓÇö the one part of the
 * control whose attributes a caller can only reach through the style map, because the library builds its `innerProps` last.
 * The **search input** cannot be marked here ΓÇö a style entry for the input only reaches the `div` around it ΓÇö and a prop
 * passed to `<Select>` is overwritten by the library's own attribute list, so it is marked by wrapping the input
 * component instead (`createReadingOnlyInput`).
 *
 * @param styles The shared styles of the mode.
 * @returns A map that also marks the popup reading-only.
 */
function readingOnlyStyles<IsMulti extends boolean>(
  styles: StylesConfig<OptionType, IsMulti, Group>,
): StylesConfig<OptionType, IsMulti, Group> {
  return {
    ...styles,
    menu: (base) => ({ ...base, 'aria-disabled': 'true' }),
  };
}

const SINGLE_READING_ONLY_STYLES = readingOnlyStyles(SELECT_STYLES);
const MULTI_READING_ONLY_STYLES = readingOnlyStyles(MULTI_STYLES);

/**
 * Lets a reading-only control say so on its **search input**.
 *
 * `react-select` 5 builds the input's attribute list itself and spreads it *after* the props of the component that draws
 * the input (`Select.js`, `renderInput`: `{...commonProps, ..., value} , ariaAttributes`), so a caller's `aria-disabled`
 * passed to `<Select>` never reaches the element ΓÇö it is overwritten, which is why this file's own attempt with a
 * `styles` entry only reached the `div` around the input as well. Wrapping the `Input` component is the supported hook:
 * the factory is called during `renderInput`, and its result is spread last by React, so whatever it puts on the
 * element stands.
 *
 * @param input Props `react-select` hands the component that draws the input.
 * @returns The same input, marked `aria-disabled` when the caller refuses a choice without disabling the field.
 */
function createReadingOnlyInput({ isAriaDisabled }: { isAriaDisabled: boolean }) {
  return function ReadingOnlyInput(input: InputProps<OptionType, boolean, Group>): ReactElement {
    const props = {
      ...input,
      ...(isAriaDisabled && !input.isDisabled ? { 'aria-disabled': true } : {}),
    } as InputProps<OptionType, boolean, Group>;

    return <components.Input {...props} />;
  };
}

const READING_ONLY_SINGLE_COMPONENTS = { Input: createReadingOnlyInput({ isAriaDisabled: true }) };
const READING_ONLY_MULTI_COMPONENTS = { Input: createReadingOnlyInput({ isAriaDisabled: true }) };

/** Props accepted by {@link AsyncSelectControl}: the form fields of {@link SelectControlProps} plus the loader. */
export interface AsyncSelectControlProps {
  /** Id of the search input, so a `<Label htmlFor>` (or the field's `id`) names the control. */
  inputId: string;
  /**
   * Id of the visible label, for a field whose label cannot point at the control's input.
   *
   * Forwarded for the same reason as on `SelectControl`: the library builds its own input id, so a `<label htmlFor>` cannot
   * be pointed at it and the field names itself with `aria-labelledby` instead.
   */
  labelId?: string;
  /** Reads one page of the list: page one on opening, then whatever the user types and scrolls to. */
  load: AsyncSelectLoader;
  /** The chosen identifier, or `null` while nothing is chosen. */
  value: string | null;
  /**
   * The row to show for {@link value} while its page is not loaded.
   *
   * A paged list cannot be searched for the selected row — that would be a request per render — so the caller supplies the
   * label of a selection it knows: a stored value being edited, or a row chosen earlier and since scrolled away. A row on
   * the loaded page still wins, because the page is the fresher answer.
   */
  selectedOption?: SelectOption | null;
  /** Receives the chosen identifier, or `null` when the choice is cleared. */
  onChange: (value: string | null) => void;
  /** Whether the value can be cleared with the cross or `Backspace`. */
  isClearable?: boolean;
  /** Whether the control accepts no input. */
  isDisabled?: boolean;
  /** Whether the control accepts no *choice* but stays readable and keeps its place in the tab order. */
  isAriaDisabled?: boolean;
  /** Text shown while nothing is selected. */
  placeholder?: string;
  /** Whether the field's answer is invalid; the control and its menu are outlined as such. */
  isInvalid?: boolean;
  /** Id of the element that explains the field, bound as `aria-describedby`-equivalent on the input. */
  messageId?: string;
  /** Name the control when no visible label exists; ignored when {@link labelId} is given. */
  ariaLabel?: string;
}

/** The rows, the paging position and the failure of one list the server holds: what the paging footer renders. */
interface SelectPaging {
  /** Rows loaded so far, in the server's order. */
  options: readonly SelectOption[];
  /** Whether the server said a page exists after the rows held. */
  hasNextPage: boolean;
  /** The failure of the last request, or `null`. */
  error: unknown;
  /** Repeats the failed request. */
  onRetry: () => void;
}

/** Everything the paging footer needs, passed down from the control that owns the hook. */
interface SelectPagingContextValue {
  /** Copy function of the active language, read at render so a locale switch rewrites the footer too. */
  t: TFunction;
  /** The list this menu is showing. */
  paging: SelectPaging;
  /** Whether a later page is being read, so the footer can say the list is still arriving. */
  isLoadingMore: boolean;
}

const SelectPagingContext = createContext<SelectPagingContextValue | null>(null);

/** The component map of the server-backed control: the library's own parts, except the menu, which carries the footer. */
const ASYNC_SELECT_COMPONENTS = { Menu: PagedMenu };

/**
 * Draws a server-backed select's menu: the library's menu and its option list, with the paging strip under both.
 *
 * **Why the menu and not the list** (TASK-0057). The list has to stay *exactly* the library's `MenuList`, because that is
 * the element the library hands its own `innerRef` to and attaches the scroll-to-bottom detector on; a custom list that wraps
 * it never receives that ref, and the detector silently binds nothing — the menu scrolls and nothing is ever asked for. The
 * menu is a safe place to extend: it already renders whatever children it is given, and the list inside it is drawn by the
 * library one level down.
 *
 * Because the option list is inside the library's `menu`, which is a block, a footer added after it would sit below a list
 * that has already grown to its content — unreachable in the viewport rather than under the rows. The list therefore gets a
 * bounded height from the `menuList` style hook and scrolls inside it, and this component makes the menu a column so the
 * strip sits under the scrolling rows instead of being pushed off the menu.
 *
 * **Where the surface is painted** (TASK-0058). The `menu` style hook paints the menu's background, border, radius and shadow,
 * and that box ends above the strip — leaving the strip as transparent text over whatever is behind the portalled menu. The
 * surface therefore belongs to the wrapper that holds both, which is why this component draws it and the control marks the
 * select with `data-paged-footer`, so the hook yields its own surface for this menu (and only this one).
 *
 * It is a module-level component reading its list **through context**, not a closure built per render: `react-select` treats
 * a new component type as a new menu, so one recreated on each keystroke would remount it and lose the scroll position the
 * next page depends on.
 *
 * @param props The menu the library is drawing plus its children, which are kept exactly as it drew them.
 * @returns The menu's surface, the library's menu inside it, and the state of the list under both.
 */
function PagedMenu({ children, ...props }: MenuProps<OptionType, false, Group>) {
  const context = useContext(SelectPagingContext);

  if (context === null) {
    // The menu is only rendered by `AsyncSelectControl`, which supplies the context; this keeps the component usable on its
    // own (and typed) without inventing an empty footer.
    return <components.Menu {...props}>{children}</components.Menu>;
  }

  const { t, paging, isLoadingMore } = context;
  const failed = paging.error !== null && paging.error !== undefined;

  return (
    // The strip lives **inside** the menu (TASK-0059): with `PagedMenu` returning it as a sibling, the library's menu box — which
    // carries `position: absolute`, `maxHeight` and `overflow: hidden` — closed above it, so the strip was drawn outside the
    // panel: it could be clipped by the menu's own overflow, and it hung below the rounded, shadowed surface instead of sitting
    // in it. Rendering it as the last child makes it the panel's own last row, and the menu's `overflow: hidden` now rounds it
    // with the rest. `flex` on the menu plus `min-h-0`/`flex-1` on the rows is what keeps it pinned: the rows take whatever
    // height is left and scroll, so the strip stays at the bottom of the menu whether there are two rows or fifty.
    <components.Menu {...props} className="flex min-h-0 flex-col">
      {children}
      <div
        // A status line, not a control, with the retry as its only action. It is inside the menu but outside the scrolling rows,
        // so it is always visible: the hint explains a gesture over the list, and it would be useless scrolled away with it.
        // The top rule is the one divider that reads as a footer rather than as another row, matching the rows' own colour.
        className="shrink-0 border-t border-border px-3 py-2 text-xs text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        {failed ? (
          <div className="flex items-start justify-between gap-2">
            <span className="text-destructive">{t('select.loadFailed')}</span>
            <button
              type="button"
              className="underline"

              // Handled on pointer-down, not on click (TASK-0057): by the time a click lands the browser has moved focus to
              // the button, the field has blurred and the library has closed the menu — discarding the retry along with the
              // list it was meant to fill. Refusing focus here leaves focus on the field the list belongs to, so neither a blur
              // nor a reopening happens, and the click still reaches the retry.
              onPointerDown={(event) => {
                event.preventDefault();
              }}
              onClick={paging.onRetry}
            >
              {t('select.retry')}
            </button>
          </div>
        ) : paging.options.length === 0 ? null : isLoadingMore ? (
          <span>{t('select.loading')}</span>
        ) : paging.hasNextPage ? (
          <span>{t('select.loadMore')}</span>
        ) : (
          <span>{t('select.allLoaded')}</span>
        )}
      </div>
    </components.Menu>
  );
}

/**
 * Renders a select whose options are read from the server one page at a time (`docs/select-inputs.md` §4).
 *
 * It is the single-choice `SelectControl` with its options replaced by the rows {@link useAsyncSelectOptions} holds, so the
 * same direction handling, copy catalogue, theme and naming rules apply. Three things are the caller's, not the control's:
 *
 * - the **loader** (`load`) is a function of search text and page and keeps no cursor, so re-opening the menu re-asks;
 * - the **selected row** may not be on any loaded page, so `selectedOption` supplies its label and a live page still wins;
 * - the **option shape** is projected by the loader, because the control does not know what a row of the caller's list is.
 *
 * A failure of the list is drawn inside the menu as its own state, with a retry, rather than thrown: a field whose rows
 * could not be read keeps its value and its place in the form.
 *
 * @param props The field's wiring plus the loader that reads its pages.
 * @returns The control with its portalled menu, the paging footer and the failure state included.
 */
export function AsyncSelectControl({
  inputId,
  labelId,
  load,
  value,
  selectedOption,
  onChange,
  isClearable = false,
  isDisabled = false,
  isAriaDisabled = false,
  placeholder,
  isInvalid = false,
  messageId,
  ariaLabel,
}: AsyncSelectControlProps) {
  const { t } = useTranslation();
  // Same direction source as `SelectControl`: the document first, so a locale switch moves the portal as well.
  const { direction } = useLocale();
  const isRtl =
    (typeof document === 'undefined' ? direction : document.documentElement.dir) !== 'ltr';

  // A locked field asks for nothing: its rows are not wanted while the save runs, and a request per locked field would be
  // traffic the user cannot see the point of.
  const paging = useAsyncSelectOptions(load, !isDisabled);

  // The loaded row wins over the caller's copy, because the page is the fresher answer; the caller's row is what keeps a
  // stored selection visible before its page arrives.
  const selected =
    paging.options.find((option) => option.value === value) ??
    (value === null || value === undefined ? null : (selectedOption ?? null));

  const noOptionsMessage = () => t('select.noOptions');
  const loadingMessage = () => t('select.loading');

  // The footer lives in the menu's list, so its list and copy travel down from here; the object is kept for the same reason
  // the component is: a provider value rebuilt on each keystroke is a new list to the library.
  const pagingContext = useMemo<SelectPagingContextValue>(
    () => ({ t, paging, isLoadingMore: paging.isLoadingMore }),
    [t, paging],
  );

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'}>
      <SelectPagingContext.Provider value={pagingContext}>
        <Select<SelectOption, false, Group>
          inputId={inputId}
          instanceId={inputId}
          aria-labelledby={labelId}
          options={[...paging.options]}
          value={selected}
          // A choice the field is not accepting is refused here, not by the DOM: see `isAriaDisabled`.
          onChange={(option) =>
            isAriaDisabled ? undefined : onChange(option === null ? null : option.value)
          }
          // The server has already searched; filtering here would search the one page held and call it a search.
          filterOption={() => true}
          // The text typed is reported, not filtered: the hook debounces it into a request from page one. The other actions
          // are the library clearing the field on its own — on a choice, on a close — and they are left to the handlers below,
          // because a `menu-close` arrives *before* `onMenuClose` and must not be answered with an extra request.
          onInputChange={(text, meta) => {
            if (meta.action === 'input-change') {
              paging.onSearch(text);
            }
          }}
          isRtl={isRtl}
          isDisabled={isDisabled}
          isLoading={paging.isLoading}
          isClearable={isClearable}
          isSearchable
          // The menu's rows are captured for the scroll-to-bottom hand-off below (TASK-0057): the library decides whether to
          // capture by guessing the device, and this menu's paging has no other way to be asked for. Stating it also makes the
          // behaviour the same on a phone and on a desktop, rather than one of the two silently having no paging.
          captureMenuScroll
          // Scrolling the rows to their end asks for the next page (TASK-0057). This is the library's own hand-off: it watches
          // the list it makes scrollable and calls this when that list is scrolled to within its own offset of the bottom, so
          // the request is attached to the element that actually scrolls. The hook refuses while a page is in flight, which is
          // what keeps a burst of trackpad events from becoming a burst of requests for the same page, and refuses when the
          // server said this was the last page, so the list stops asking instead of walking off the end.
          onMenuScrollToBottom={paging.onLoadMore}
          // A closed menu goes back to the unfiltered first page (TASK-0057). The library clears the search field itself on
          // every close — a choice, a click outside, `Escape`, a blur — so the field the user next opens is empty, and the rows
          // have to agree with it: without this, a search that found nothing left an empty list standing under an empty input,
          // so reopening the menu showed a list that could not be re-read. The reset is fired with the library's own close,
          // after the choice has been reported to `onChange`, so a selection is already made and rows cannot undo it.
          onMenuClose={paging.onReset}
          components={ASYNC_SELECT_COMPONENTS}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          formatOptionLabel={(option, meta) => renderOption(t, option, meta.context)}
          placeholder={placeholder}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          // The list scrolls inside a bounded height, which is the first of the three things a scroll-to-load menu needs
          // (TASK-0057): without a finite `maxMenuHeight` the library computes no `maxHeight` for the list, and a list with no
          // `maxHeight` grows to hold every row it has and never scrolls — so the footer printed a hint for a gesture the menu
          // could not perform. The opening page is five rows plus the paging strip beneath them, so it fits without a
          // scrollbar and the sixth row is the first one a scroll reveals.
          maxMenuHeight={ASYNC_SELECT_MAX_MENU_HEIGHT}
          {...{
            'aria-invalid': isInvalid || isAriaDisabled || undefined,
            'aria-errormessage': messageId,
            'aria-disabled': isAriaDisabled && !isDisabled ? true : undefined,
          }}
          aria-label={ariaLabel}
          // The menu outlives the page it was opened on: a fixed position against the viewport keeps it whole over a card.
          menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
          menuPosition="fixed"
          styles={isAriaDisabled && !isDisabled ? SINGLE_READING_ONLY_STYLES : SELECT_STYLES}
          classNamePrefix="forge-select"
          theme={selectTheme}
        />
      </SelectPagingContext.Provider>
      {isDisabled ? (
        <DisabledValue inputId={inputId} text={selected?.label ?? placeholder ?? ''} />
      ) : null}
    </div>
  );
}

/** Props accepted by {@link SelectControl}. */
export interface SelectControlProps {
  /** Id of the search input, so a `<Label htmlFor>` (or the field's `id`) names the control. */
  inputId: string;
  /**
   * Id of the visible label, for a field whose label cannot point at the control's input.
   *
   * `react-select` generates its own search input id, so a `<label htmlFor>` cannot be pointed at it; `FormField` hands
   * its label's id here instead and the input names itself with `aria-labelledby`. When both are given the label wins,
   * because a native `<label for>` association cannot be forged on an element the caller does not render.
   */
  labelId?: string;
  /** Options the user may pick; the value of each is the domain identifier. */
  options: readonly SelectOption[];
  /**
   * Selected identifier, or `null` for nothing chosen.
   *
   * In multi-value mode this is the **first** choice and only reads back a selection the caller keeps elsewhere; the
   * property to use there is {@link SelectControlProps.values}. A locked field (see {@link SelectControlProps.isDisabled})
   * is the exception: it reads the value it announces from this property even in multi-value mode, because that is the
   * only value the caller is guaranteed to pass.
   */
  value: string | string[] | null;
  /**
   * Accessible name of the control, for a field whose label is a table column header, a card title or nothing at all.
   *
   * Passed to the library as `aria-label`, which is the one attribute of the input it forwards verbatim: `review-table`
   * and the card layouts name a field that has no `<label>` element of its own.

   */
  ariaLabel?: string;
  /**
   * Selected identifiers in multi-value mode, in display order.
   *
   * Kept apart from {@link SelectControlProps.value} so the single-choice signature stays a single identifier: a control
   * that reported a list to a caller expecting one identifier would type-check only through a cast.
   */
  values?: readonly string[] | null;
  /** Receives the selected identifier, or `null` when the choice was cleared. */
  onChange: (value: string | null) => void;
  /**
   * Whether the control is worth reading but not currently accepting a choice.
   *
   * `react-select` has one switch ΓÇö `isDisabled` ΓÇö and it does two things: it refuses the choice *and* it drops the control
   * from the accessibility tree (a disabled input announces nothing). The two causes of refusal in this product are not the
   * same: a save in flight or a frozen record locks the whole field, while a field whose parent is still unchosen is a real
   * question the user can see and expects to answer once the parent is set. That second case is this flag: the control
   * keeps its name, its value and its place in the tab order, the choice is refused in the handlers, and the field is
   * reported through `aria-invalid` and `aria-disabled` on its input (the latter through `createReadingOnlyInput`, because
   * the library overwrites its input's attributes), while the popup it opens carries `aria-disabled` as well
   * (`readingOnlyStyles`).
   */
  isAriaDisabled?: boolean;
  /** Text shown while nothing is selected. */
  placeholder?: string;
  /** Whether the control accepts no input. */
  isDisabled?: boolean;
  /** Whether the list is still being read; the control shows its loading answer instead of "no options". */
  isLoading?: boolean;
  /** Whether the chosen value can be cleared with the cross or `Backspace`. */
  isClearable?: boolean;
  /**
   * Whether the field accepts several choices at once.
   *
   * The control then reports the **list of identifiers** through {@link SelectControlProps.onChangeMulti} and renders one
   * removable chip per choice. Selection order is kept, because the order carries meaning in a wizard answer and in a
   * prompt's classification lists.
   */
  isMulti?: boolean;
  /**
   * Receives the chosen identifiers, in selection order, in multi-value mode.
   *
   * Only read when {@link SelectControlProps.isMulti} is set; {@link SelectControlProps.onChange} is then unused.
   */
  onChangeMulti?: (values: string[]) => void;
  /**
   * Label of a chip's remove button, as a function of the chip's label.
   *
   * The library's default is the English `"Remove {label}"`, which would appear in the middle of a Persian screen; the
   * call site passes a formatter backed by the copy catalogue.
   */
  removeLabel?: (label: string) => string;
  /**
   * Id of the element that explains the field ΓÇö a hint or an error ΓÇö exposed through `aria-errormessage`.
   *
   * The element itself is rendered by the caller, because `FormField` owns that contract and also decides whether the
   * text is a hint or a refusal. (`aria-describedby` is not forwarded by `react-select` 5; see the module note.)
   */
  messageId?: string;
  /** Whether the field failed validation, which marks the search input `aria-invalid`. */
  isInvalid?: boolean;
  /** Extra class for the outer element. */
  className?: string;
  /**
   * Accessible name of the control, for a field whose label is a table column header, a card title or nothing at all.
   *
   * A field that has a visible `<label>` element passes its id as {@link SelectControlProps.labelId} instead; this prop is
   * for the case where the name exists only for assistive technology.
   */
  'aria-label'?: string;
}

/**
 * Renders a searchable select over a list the caller already holds: one choice by default, several with
 * {@link SelectControlProps.isMulti}.
 *
 * The control reports the **identifier** of the chosen option (or the identifiers, in selection order, in multi-value
 * mode) and nothing else; mapping that identifier to and from the API's payload is the caller's job, as is deciding
 * whether a `null` is sent as an omitted member or as `""`.
 *
 * @param props Options, selection, and the accessibility wiring of the field.
 * @returns The control, its portalled menu included.
 */
export function SelectControl({
  inputId,
  labelId,
  options,
  value: valueProp,
  values,
  onChange,
  placeholder,
  isDisabled = false,
  isAriaDisabled = false,
  isLoading = false,
  isClearable = false,
  isMulti = false,
  onChangeMulti,
  removeLabel,
  messageId,
  isInvalid = false,
  className,
  'aria-label': ariaLabel,
}: SelectControlProps) {
  const { t } = useTranslation();
  // The document is the single source of truth for direction (`i18n.ts` writes `dir` whenever the locale changes), so it
  // is read first; `useLocale` is the fallback for a render that happens before that attribute exists.
  const { direction } = useLocale();
  const isRtl =
    (typeof document === 'undefined' ? direction : document.documentElement.dir) !== 'ltr';

  // A locked control has no render of its own to read: `react-select` renders its chosen row only while it is usable, so
  // a disabled field would show an empty box ΓÇö and describe itself with nothing ΓÇö unless the caller passes the
  // *controlled* value (`value`) rather than the multi-mode `values` list.
  const value = valueProp ?? null;

  // The selected row is looked up by identifier rather than kept as an option object: the options array is rebuilt when
  // the query answers or the locale changes, and an object held across that rebuild would keep the old locale's label.
  const selected =
    options.find(
      (option) => option.value === (Array.isArray(value) ? (value[0] ?? null) : value),
    ) ?? null;

  // The control's own answers, in the interface language: the library's defaults are English sentences that would appear
  // in the middle of a Persian screen.
  const noOptionsMessage = () => t('select.noOptions');
  const loadingMessage = () => t('select.loading');

  return (
    // The wrapper owns the direction: `react-select` does not forward an arbitrary `dir` attribute (its container element
    // receives only an `id` and a key handler), and it applies `isRtl` to its own layout. Setting `dir` here is what makes
    // the menu portal ΓÇö appended to `document.body`, outside this tree ΓÇö inherit the reading direction as well.
    <div dir={isRtl ? 'rtl' : 'ltr'} className={className}>
      {isMulti ? (
        <>
          <SelectMulti
            inputId={inputId}
            labelId={labelId}
            options={options}
            controlledValue={value}
            values={values}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isAriaDisabled={isAriaDisabled}
            isLoading={isLoading}
            isClearable={isClearable}
            noOptionsMessage={noOptionsMessage}
            loadingMessage={loadingMessage}
            messageId={messageId}
            isInvalid={isInvalid}
            ariaLabel={ariaLabel}
            t={t}
            onChangeMulti={onChangeMulti}
            removeLabel={removeLabel}
          />
          {isDisabled ? (
            <DisabledValue
              inputId={inputId}
              // A locked field has no render of its own to read ΓÇö the library draws no chips for a disabled control ΓÇö so
              // the chosen rows are read out beside it, joined the way the chips would have read.
              text={labelTexts(
                options.filter((option) => (values ?? []).includes(option.value)),
                placeholder,
              )}
            />
          ) : null}
        </>
      ) : (
        <Select<SelectOption, false, Group>
          inputId={inputId}
          instanceId={inputId}
          aria-labelledby={labelId}
          options={[...options]}
          value={selected}
          // A choice the field is not accepting is refused here, not by the DOM: see `isAriaDisabled`.
          onChange={(option) =>
            isAriaDisabled ? undefined : onChange(option === null ? null : option.value)
          }
          isRtl={isRtl}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable
          filterOption={(candidate, term) => matchesOptionTerm(candidate.data, term)}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          formatOptionLabel={(option, meta) => renderOption(t, option, meta.context)}
          placeholder={placeholder}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          // A reading-only field is refused in its handlers and reported through `aria-invalid`, and it keeps its input in
          // the tree while saying it takes no choice through `aria-disabled`. All three travel as props rather than as a
          // style map: the library spreads them verbatim onto the input, whereas a style entry only reaches the `div`
          // around it and a kebab-case key there is not a CSS property. `aria-errormessage` is a valid attribute but not a
          // valid JSX prop name, so the object is spread instead of listed.
          {...{
            'aria-invalid': isInvalid || isAriaDisabled || undefined,
            'aria-errormessage': messageId,
            'aria-disabled': isAriaDisabled && !isDisabled ? true : undefined,
          }}
          // `react-select` writes its own input attributes last, so the flag above is only a type-level declaration of the
          // intent held by `createReadingOnlyInput`; `components` is what actually puts it on the element.
          components={isAriaDisabled && !isDisabled ? READING_ONLY_SINGLE_COMPONENTS : undefined}
          // The control is never named by its value or its placeholder: the name comes from the caller ΓÇö the visible
          // `<label>` (`aria-labelledby`) or `aria-label` ΓÇö which is what `docs/select-inputs.md` ┬º5 requires.
          aria-label={ariaLabel}
          // The menu is portalled and positioned against the viewport, so neither a card, a scroll container nor the bottom
          // of the page can clip it. The target is resolved at render time: it has to be an element of the live document.
          menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
          menuPosition="fixed"
          styles={isAriaDisabled && !isDisabled ? SINGLE_READING_ONLY_STYLES : SELECT_STYLES}
          classNamePrefix="forge-select"
          theme={selectTheme}
        />
      )}
      {isDisabled ? (
        <DisabledValue inputId={inputId} text={selected?.label ?? placeholder ?? ''} />
      ) : null}
    </div>
  );
}
/** Props of {@link SelectMulti}: the fields of {@link SelectControlProps} it needs, already resolved. */
interface MultiSelectProps {
  inputId: string;
  labelId?: string;
  options: readonly SelectOption[];
  /** See {@link SelectControlProps.controlledValue}: the value that survives a locked control. */
  controlledValue?: string | string[] | null;
  values?: readonly string[] | null;
  onChangeMulti?: (values: string[]) => void;
  removeLabel?: (label: string) => string;
  placeholder?: string;
  isDisabled: boolean;
  /** See {@link SelectControlProps.isAriaDisabled}: refused through the handler, kept in the accessibility tree. */
  isAriaDisabled: boolean;
  isLoading: boolean;
  isClearable: boolean;
  noOptionsMessage: () => string;
  loadingMessage: () => string;
  messageId?: string;
  isInvalid: boolean;
  /** Copy function of the active language, so the chip buttons follow a locale switch. */
  t: TFunction;
  /** Accessible name the caller gave the field, when it has no visible label element. */
  ariaLabel?: string;
}

/**
 * The multi-value control of {@link SelectControl}.
 *
 * It is a separate component rather than another branch inside the single-choice one because `react-select` types
 * `value`, `onChange`, `styles` and `components` by the `isMulti` flag: rendering both shapes from one call would need a
 * cast on each of them, and a cast is exactly what hides a wrong prop.
 *
 * @param props The fields of {@link SelectControlProps} it needs, with the strings the wrapper resolved.
 * @returns The chip field, its portalled menu, and the read-only text that names the value of a locked field.
 */
function SelectMulti({
  inputId,
  labelId,
  options,
  controlledValue,
  values,
  onChangeMulti,
  removeLabel,
  placeholder,
  isDisabled,
  isAriaDisabled,
  isLoading,
  isClearable,
  noOptionsMessage,
  loadingMessage,
  messageId,
  isInvalid,
  t,
  ariaLabel,
}: MultiSelectProps) {
  const { direction } = useLocale();
  const isRtl =
    (typeof document === 'undefined' ? direction : document.documentElement.dir) !== 'ltr';

  // The chosen rows are looked up by identifier, for the reason the single mode does it: the options array is rebuilt on
  // every answer or locale change, and a held object would keep the old locale's label. `null`/`[]` is "nothing is
  // stored"; the standalone `""` row some lists carry is a real option, so it is selected when it is a stored value.
  // `values` wins over the controlled value; a plain identifier (or a list it may have been widened to) is read as a one-item
  // list.
  const selectedValues =
    values ??
    (Array.isArray(controlledValue)
      ? controlledValue
      : controlledValue === null || controlledValue === undefined
        ? []
        : [controlledValue]);
  const selected = options.filter((option) => selectedValues.includes(option.value));

  // The chip copy is the library's own component map, and the map is fixed when the control mounts, so the button's label
  // is formatted in this scope and carried in through `key`-stable props rather than read from state inside the button:
  // a *createElement* component is invoked once, so a value read in its body would freeze the first language into the
  // button. `t` is the only thing that rebuilds the map, hence the bare dependency.
  const removeLabelText = useMemo(
    () => (label: string) =>
      (removeLabel ?? ((text: string) => t('select.remove', { label: text }) as string))(label),
    [removeLabel, t],
  );

  const chipComponents = useMemo(
    () => ({
      // The library's own remove button is a `div[role=button]` whose name it hardcodes as `"Remove {label}"`, and it
      // hands the chip's removal handler to the component as `removeProps`. Only the `className` and the label can be
      // replaced: the handler has to stay the library's, so the customisation keeps `removeProps` and renames the control.
      // `aria-label` then wins over the content ΓÇö `MultiValue` passes the chip's label as `children`, an SVG contributing
      // no text ΓÇö so the button is announced in the interface language instead of the library's English sentence.
      MultiValueRemove: (props: MultiValueRemoveProps<SelectOption, true, Group>) => (
        <components.MultiValueRemove
          {...props}
          innerProps={{ ...props.innerProps, 'aria-label': removeLabelText(props.data.label) }}
        />
      ),
    }),
    [removeLabelText],
  );

  // The same map, plus the reading-only flag on the popup: with no menu open the popup holds nothing (the chosen values are
  // chips or a placeholder), so the popup is the one listbox a screen reader can find and the only place the flag is useful.
  // It is kept as its own object because the library rebuilds the control whenever the map's identity changes.
  const menuComponents = useMemo(
    () =>
      isAriaDisabled && !isDisabled
        ? {
            ...chipComponents,
            ...READING_ONLY_MULTI_COMPONENTS,
            Menu: (props: MenuProps<SelectOption, true, Group>) => (
              <components.Menu
                {...props}
                innerProps={{ ...props.innerProps, 'aria-disabled': 'true' }}
              />
            ),
          }
        : chipComponents,
    [chipComponents, isAriaDisabled, isDisabled],
  );

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'}>
      <Select<SelectOption, true, Group>
        inputId={inputId}
        instanceId={inputId}
        aria-labelledby={labelId}
        options={[...options]}
        isMulti
        value={selected}
        // A choice the field is not accepting is refused here, not by the DOM: see `isAriaDisabled`.
        onChange={(chosen) =>
          isAriaDisabled ? undefined : onChangeMulti?.((chosen ?? []).map((option) => option.value))
        }
        isRtl={isRtl}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isSearchable
        filterOption={(candidate, term) => matchesOptionTerm(candidate.data, term)}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        formatOptionLabel={(option, meta) => renderOption(t, option, meta.context)}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={loadingMessage}
        // See the single-choice branch: the state attributes travel as one spread object, and the popup is marked by the
        // style map as well, because it is the field's only listbox rather than its input.
        {...{
          'aria-invalid': isInvalid || isAriaDisabled || undefined,
          'aria-errormessage': messageId,
          'aria-disabled': isAriaDisabled && !isDisabled ? true : undefined,
        }}
        // A chip is removed by its own button, so the field needs no extra name while it is usable. The name is never the
        // selected value ΓÇö see the single-choice branch.
        aria-label={ariaLabel}
        // The popup is the field's only listbox, so it is where a reading-only control says it is reading-only rather than
        // removing itself from the accessibility tree.
        components={menuComponents}
        menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
        menuPosition="fixed"
        styles={isAriaDisabled && !isDisabled ? MULTI_READING_ONLY_STYLES : MULTI_STYLES}
        classNamePrefix="forge-select"
        theme={selectTheme}
      />
    </div>
  );
}

/**
 * Joins the chosen labels into one sentence, falling back to the placeholder when nothing is chosen.
 *
 * @param selected The chosen options, in selection order.
 * @param placeholder The control's placeholder, when the caller gave one.
 * @returns The text a locked control is announced with.
 */
function labelTexts(selected: readonly SelectOption[], placeholder?: string): string {
  if (selected.length === 0) {
    return placeholder ?? '';
  }

  return selected.map((option) => option.label).join(', ');
}

/**
 * Overlays the library's colour theme with the design tokens.
 *
 * The theme drives the parts whose colours the library computes rather than exposes through `styles` (the focus ring of
 * a selected option, the disabled text, the clear indicator's hover). Mapping `neutral*` onto the token scale is what
 * keeps dark mode coherent: without it the menu would keep the library's light greys on a dark surface.
 *
 * @param theme The library's default theme.
 * @returns The theme with the application's tokens.
 */
function selectTheme(theme: Theme): Theme {
  return {
    ...theme,
    borderRadius: 8,
    colors: {
      ...theme.colors,
      primary: 'hsl(var(--primary))',
      primary75: 'hsl(var(--primary) / 0.75)',
      primary50: 'hsl(var(--primary) / 0.5)',
      primary25: 'hsl(var(--primary) / 0.25)',
      danger: 'hsl(var(--destructive))',
      dangerLight: 'hsl(var(--destructive) / 0.2)',
      neutral0: 'hsl(var(--popover))',
      neutral5: 'hsl(var(--muted))',
      neutral10: 'hsl(var(--border))',
      neutral20: 'hsl(var(--border))',
      neutral30: 'hsl(var(--input))',
      neutral40: 'hsl(var(--muted-foreground))',
      neutral50: 'hsl(var(--muted-foreground))',
      neutral60: 'hsl(var(--muted-foreground))',
      neutral70: 'hsl(var(--popover-foreground))',
      neutral80: 'hsl(var(--popover-foreground))',
      neutral90: 'hsl(var(--popover-foreground))',
    },
  };
}

/**
 * Renders the chosen value of a disabled control.
 *
 * A disabled input is removed from the accessibility tree in most screen readers, so its selected label is otherwise
 * unreadable ΓÇö and this product locks controls while a save is in flight or until a parent choice is made, which is
 * exactly when the user needs to know what the field holds. The element is bound to the input with `aria-describedby`, is
 * read-only text (no interaction is claimed) and is part of the layout nowhere: it is announced, not drawn, because the
 * control already draws the value.
 *
 * @param props Control identifier and the text to announce.
 * @returns The visually hidden element, or nothing when it has no text.
 */
function DisabledValue({ inputId, text }: { inputId: string; text: string }) {
  if (text.length === 0) {
    return null;
  }

  return (
    <span id={disabledValueId(inputId)} className="sr-only">
      {text}
    </span>
  );
}

/**
 * Renders one option for the menu and for the selected value.
 *
 * The two contexts are deliberately different. In the **menu** an option the list no longer offers carries its
 * "(unavailable)" note, so the row that explains a stored selection is visible where the user is looking; in the
 * **value** the note would sit inside the field and read as part of the name, so only the label is shown.
 *
 * @param t Translation function, for the note.
 * @param option Option to render.
 * @param context Whether the option is drawn in the menu or as the chosen value.
 * @returns The option's label, with the note when it applies.
 */
function renderOption(t: TFunction, option: SelectOption, context: 'menu' | 'value'): ReactNode {
  if (option.isUnavailable !== true || context !== 'menu') {
    return option.label;
  }

  return (
    <div className="flex flex-wrap items-baseline gap-1">
      <span>{option.label}</span>
      <span className="text-xs text-muted-foreground">({t('select.unavailable')})</span>
    </div>
  );
}
